import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from "react-beautiful-dnd"
import { css, cx } from "@emotion/css"

import DeleteIcon from "@material-ui/icons/Cancel"
import IconButton from "ui/buttons/IconButton"
import React from "react"
import { useI18n } from "providers/I18n"

export type DraggableItem = {
  id: string
  name: string
  semanticCode?: string
  type?: string
  recordId?: string
  disabled?: boolean
}

type Props = {
  items: Array<DraggableItem>
  renderItem: (item: DraggableItem) => JSX.Element
  onReorder: (items: Array<DraggableItem>) => void
  activeId?: string
  direction?: "horizontal" | "vertical"
  itemClassName?: string
  onDelete?: (id: string, itemId?: string) => void
  onDeleteTitle?: string
  isDeletable?: (item: any) => Boolean
}

const reorder = (
  list: Array<DraggableItem>,
  startIndex: number,
  endIndex: number,
) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  return result
}

export const DraggableItems = ({
  items,
  renderItem,
  onReorder,
  activeId,
  direction = "horizontal",
  itemClassName,
  onDelete,
  onDeleteTitle,
  isDeletable = (_) => true,
}: Props) => {
  const { t } = useI18n()

  const [modalInfo, setModalInfo] = React.useState<ModalInfo>(undefined)
  const isModalOpen = Boolean(modalInfo)

  const onDragEnd = (result: DropResult) => {
    if (result.destination) {
      const newItems = reorder(
        items,
        result.source.index,
        result.destination.index,
      )
      onReorder(newItems)
    }
  }

  const handleDelete = (item: any) => {
    if (!!onDelete) onDelete(item?.key || item.id, item?.recordId)
    setModalInfo(undefined)
  }

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable" direction={direction}>
          {(provided) => (
            <div
              className={styles.droppable(direction)}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {items.map((item, index) => (
                <Draggable key={item.id} draggableId={item.id} index={index}>
                  {(provided_, snapshot) => (
                    <div
                      data-testid={`media-plan-draggable-item-${index + 1}`}
                      ref={provided_.innerRef}
                      className={cx([
                        styles.draggable,
                        itemClassName,
                        snapshot.isDragging ? "dragging" : "",
                        activeId === item.id ? "active" : "",
                      ])}
                      {...provided_.draggableProps}
                      {...provided_.dragHandleProps}
                    >
                      {renderItem(item)}
                      {!!onDelete && isDeletable(item) ? (
                        <IconButton
                          title={onDeleteTitle}
                          className={styles.deleteButton}
                          onClick={() =>
                            setModalInfo({
                              type: "secondary_destructive",
                              submitLabel: t("modal.label.delete"),
                              cancelLabel: t("modal.label.cancel"),
                              onSubmit: () => handleDelete(item),
                              title: t("item.delete.title"),
                              description: t("item.delete.description"),
                            })
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <ConfirmationModal
        onCancel={() => setModalInfo(undefined)}
        open={isModalOpen}
        {...modalInfo}
      />
    </>
  )
}

const styles = {
  droppable: (direction: "vertical" | "horizontal") => css`
    display: grid;
    grid-auto-flow: ${direction === "vertical" ? "row" : "column"};
    grid-auto-columns: minmax(100px, max-content);
    align-items: center;
  `,
  draggable: css`
    display: grid;
    grid-auto-flow: column;
    grid-template-columns: 1fr max-content;
    align-items: center;
  `,
  deleteButton: css`
    --btn-padding: 0;
    --btn-bg-hover: transparent;
    --btn-border-color-hover: transparent;
    --btn-focus-ring-color: transparent;
    .icon {
      border-radius: 30px;
      width: 1rem;
      height: 1rem;
      line-height: 1;
      color: var(--dark-grey);
    }
  `,
}
